import { useEffect, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import FilterTabs from "../shared/FilterTabs";

/**
 * Sunucunun rapor `sheets` çıktısını Excel'deki gibi sayfa sekmeleriyle
 * tablo olarak gösterir (#338). İlk satır başlık, "TOPLAM" ile başlayan
 * satır toplam satırı — indirilen .xlsx ile aynı içerik.
 */

const isTotalRow = (row) => String(row?.[0] ?? "").toLocaleUpperCase("tr") === "TOPLAM";

const formatCell = (value) =>
  typeof value === "number"
    ? value.toLocaleString("tr-TR", { maximumFractionDigits: 1 })
    : String(value ?? "");

/**
 * Sütun yalnız sayılardan oluşuyorsa sağa, metin karışıyorsa (Özet'teki
 * "Değer" gibi) tamamen sola yaslanır — aynı sütunda değerler sağa sola
 * dağılmasın. Başlık da sütunun hizasını alır.
 */
const numericColumns = (head, body) =>
  head.map((_, c) => {
    const values = body.map((row) => row?.[c]).filter((v) => v !== "" && v != null);
    return values.length > 0 && values.every((v) => typeof v === "number");
  });

export default function ReportSheetsPreview({ sheets = [] }) {
  const usable = (Array.isArray(sheets) ? sheets : []).filter(
    (s) => Array.isArray(s?.rows) && s.rows.length > 0
  );
  const [active, setActive] = useState(0);

  // Yeni rapor gelince ilk sayfaya dön
  useEffect(() => setActive(0), [sheets]);

  if (usable.length === 0) {
    return (
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center bg-white rounded-xl border border-gray-100">
        <FaFileExcel className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">Excel önizlemesi yok.</p>
      </div>
    );
  }

  const sheet = usable[Math.min(active, usable.length - 1)];
  const [head, ...body] = sheet.rows;
  const numeric = numericColumns(head, body);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <FilterTabs
        filters={usable.map((s, i) => ({ id: i, label: s.name }))}
        activeFilter={Math.min(active, usable.length - 1)}
        onChange={setActive}
        color="emerald"
      />
      <div className="flex-1 min-h-0 overflow-auto bg-white rounded-xl border border-gray-100">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr>
              {head.map((cell, c) => (
                <th
                  key={c}
                  className={`px-3 py-2 bg-emerald-700 text-white font-SemiBold whitespace-nowrap ${
                    c === 0 ? "text-left sticky left-0 z-20" : numeric[c] ? "text-right" : "text-left"
                  }`}
                >
                  {formatCell(cell)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, r) => {
              const total = isTotalRow(row);
              return (
                <tr
                  key={r}
                  className={total ? "bg-gray-100 font-SemiBold" : "border-b border-gray-100"}
                >
                  {head.map((_, c) => (
                    <td
                      key={c}
                      className={`px-3 py-1.5 whitespace-nowrap ${
                        numeric[c] ? "text-right tabular-nums" : "text-left"
                      } ${c === 0 ? `sticky left-0 ${total ? "bg-gray-100" : "bg-white"}` : ""}`}
                    >
                      {formatCell(row[c])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
